import React from "react";
import { useSelector } from "react-redux";
import type { IApplicationState } from "../Redux/Reducers";
import { usePageHeaderActionsState } from "./PageHeaderActionsProvider";
import { PresenceBar } from "../Realtime/PresenceBar";
import { DeployProgressBar } from "./DeployProgressBar";
import { NotificationBell } from "./NotificationBell";

type PageHeaderProps = {
	actions?: React.ReactNode;
	sticky?: boolean;
};

/** Sticky page header displaying the current page title, the live presence bar (who is online, on
 *  every page), and optional action buttons from Redux or props. */
const PageHeader: React.FC<PageHeaderProps> = ({ actions, sticky = true }) => {
	const header = useSelector((state: IApplicationState) => state.pageHeader);
	const contextualActions = usePageHeaderActionsState();
	// nowrap + shrink-0: the actions are the one thing that must never fold onto a second line or be
	// squeezed : the title yields instead (see below).
	const renderedActions = (
		<div className="flex shrink-0 flex-nowrap items-center justify-end gap-2">
			{actions ?? contextualActions}
		</div>
	);

	// Vertical padding scales with the viewport, so the bar gets out of the way on small screens
	// instead of costing a fixed 32px of chrome. Arbitrary clamp() values on purpose: they compile to
	// unique class names, which cannot be clobbered by the second Tailwind build the renderer
	// stylesheet drags in (a plain utility from that later sheet beats an earlier responsive variant : // the bug that silently killed xl:grid-cols-2 on /account).
	const containerClasses = sticky
		? "sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 sm:px-6 py-[clamp(0.5rem,1.1vw,1rem)] shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/80"
		: "border-b border-slate-200 bg-white px-4 sm:px-6 py-[clamp(0.5rem,1.1vw,1rem)]";

	return (
		<div className={containerClasses}>
			{/* No flex-wrap: one line, always. */}
			<div className="flex items-center justify-between gap-3">
				{/* min-w-0 + truncate : a long page title shortens with an ellipsis rather than shoving the
				    actions off the bar or onto a second row. */}
				<div className="min-w-0 flex-1">
					{header.title && (
						<p className="truncate text-[clamp(0.9rem,1.1vw,1.125rem)] font-semibold text-slate-900">
							{header.title}
						</p>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-[clamp(0.5rem,1vw,1rem)]">
					{/* Ordre imposé : progression de déploiement, avatars de présence (sa propre photo
					    en dernier, anneau vert = en ligne), puis la CLOCHE collée à droite de la photo ;
					    les boutons d'action contextuels restent tout à droite comme avant. */}
					<DeployProgressBar />
					<PresenceBar />
					<NotificationBell />
					{renderedActions}
				</div>
			</div>
		</div>
	);
};

export default PageHeader;
